/**
 * feat-338 — Section 3, Stores and lesson time + the Lessons table (design 01, NOTES §5).
 *
 * Every number is the API's. The only client logic is display: a shared time-bar scale
 * per table, and "notably worse than all stores" shading (DESIGN-QUESTION Q2 constants
 * in questionAnalysisFormat.ts), worse only.
 */
import React from 'react';
import type { DrillTime, LocationTime, OverallTime, TimeResponse } from '../../../../services/questionAnalysisService';
import {
  C,
  StoreMetricKey,
  formatMinutes,
  formatTargetMinutes,
  isNotablyWorse,
  pct,
  pctTitle,
  timeScaleMax,
} from './questionAnalysisFormat';
import { orDash, SuppressedValue } from './parts';

export const STORES_COPY = {
  storesTitle: 'Stores and lesson time',
  storesSub: (target: string) => ({
    a: 'Target: ',
    b: `under ${target} per lesson`,
    c: '. Time counts only while the app is open, across both passes. Every store is listed, whatever the Store filter says. Learners count toward the store they belong to today.',
  }),
  legendTypical: 'Typical (median)',
  legendP90: 'Up to the slowest 1 in 10 (90th percentile)',
  legendTarget: (target: string) => `${target.replace(' min', '-minute')} target`,
  legendWorse: 'Notably worse than all stores',
  worseSr: ' (notably worse than all stores)',
  allStores: 'All stores',
  tenantOverall: 'tenant overall',
  lessonsTitle: 'Lessons',
  lessonsSub: 'Longest lessons first (by the slowest 1 in 10). Follows the Store, Language and Dates filters.',
  noLocations: 'No stores for this tenant.',
  noLessons: 'No lessons in this range.',
  flagged: (n: number) => `${n} flagged`,
  loading: 'Loading lesson times…',
} as const;

const thNum = (label: string[], title: string, active = false) => (
  <th
    scope="col"
    aria-sort={active ? 'descending' : undefined}
    className={`${C.th} text-right ${active ? 'text-ink' : 'text-inkMuted'}`}
    title={title}
  >
    {label.map((l, i) => (
      <React.Fragment key={l}>
        {i > 0 && <br />}
        {l}
      </React.Fragment>
    ))}
    {active && (
      <>
        {' '}
        <span aria-hidden="true" className="text-[10px]">
          ▼
        </span>
      </>
    )}
  </th>
);

const pctOf = (v: number, max: number) => `${Math.min(100, (v / max) * 100).toFixed(1)}%`;

export const TimeBar: React.FC<{ med: number | null; p90: number | null; scale: number; target: number }> = ({
  med,
  p90,
  scale,
  target,
}) => (
  <div className="relative h-3 w-[180px] rounded-full bg-cream-dark" aria-hidden="true" data-testid="time-bar">
    {p90 != null && <div className="absolute inset-y-0 left-0 rounded-full bg-ink/20" style={{ width: pctOf(p90, scale) }} />}
    {med != null && <div className="absolute inset-y-0 left-0 rounded-full bg-ink/70" style={{ width: pctOf(med, scale) }} />}
    <div className="absolute -top-1 -bottom-1 w-0.5 bg-gold" style={{ left: pctOf(target, scale) }} data-testid="target-marker" />
  </div>
);

export const ScaleAxis: React.FC<{ scale: number; target: number }> = ({ scale, target }) => {
  const ticks: number[] = [];
  for (let m = 0; m <= scale / 60000; m += 10) ticks.push(m);
  const targetMin = target / 60000;
  return (
    <div className="relative h-4 w-[180px] text-[10px] text-inkMuted normal-case tracking-normal font-semibold" data-testid="scale-axis">
      {ticks.map((m) => (
        <span key={m} className="absolute -translate-x-1/2" style={{ left: pctOf(m * 60000, scale) }}>
          {m === targetMin ? <span className="text-ink font-bold">{formatTargetMinutes(target)}</span> : m}
        </span>
      ))}
      {!ticks.includes(targetMin) && (
        <span className="absolute -translate-x-1/2 -top-3" style={{ left: pctOf(target, scale) }}>
          <span className="text-ink font-bold">{formatTargetMinutes(target)}</span>
        </span>
      )}
    </div>
  );
};

const Cell: React.FC<{ worse?: boolean; title?: string; bold?: boolean; children: React.ReactNode }> = ({
  worse = false,
  title,
  bold = false,
  children,
}) => (
  <td
    className={`${C.tdNum} text-sm ${worse ? `${C.worse} font-bold` : ''} ${bold ? 'font-bold' : ''}`}
    title={title}
    data-worse={worse ? 'true' : undefined}
  >
    {children}
    {worse && (
      <>
        <span className="sr-only">{STORES_COPY.worseSr}</span>
        <span aria-hidden="true" className="ml-1 text-[10px] text-ink">
          ▲
        </span>
      </>
    )}
  </td>
);

const StoreRow: React.FC<{
  row: LocationTime | OverallTime;
  name: string;
  overall: OverallTime | null;
  scale: number;
  target: number;
}> = ({ row, name, overall, scale, target }) => {
  const isOverall = overall === null;
  const rowBg = isOverall ? 'bg-cream' : 'bg-white';
  const nameCell = (
    <th
      scope="row"
      className={`${C.tdFrozen} ${rowBg} w-[180px] min-w-[180px] text-left text-sm ${isOverall ? 'font-bold' : 'font-semibold'} text-ink`}
    >
      {name}
      {isOverall && <span className="block text-[11px] font-normal text-inkMuted">{STORES_COPY.tenantOverall}</span>}
    </th>
  );
  if (row.suppressed) {
    return (
      <tr className={rowBg} data-testid="store-row" data-suppressed="true">
        {nameCell}
        <td colSpan={8} className={`${C.td} text-sm`}>
          <SuppressedValue inline />
        </td>
      </tr>
    );
  }
  const ov = overall && !overall.suppressed ? overall : null;
  const w = (k: StoreMetricKey) => !!ov && isNotablyWorse(k, row[k], ov[k]);
  return (
    <tr className={`${rowBg} ${isOverall ? 'font-semibold' : ''} text-ink`} data-testid={isOverall ? 'overall-row' : 'store-row'}>
      {nameCell}
      <Cell>{row.learners}</Cell>
      <Cell>{orDash(row.lessons_completed)}</Cell>
      <Cell worse={w('first_try_correct_rate')} title={pctTitle(row.first_try_correct_rate)}>
        {orDash(pct(row.first_try_correct_rate))}
      </Cell>
      <Cell worse={w('median_lesson_active_ms')}>{orDash(formatMinutes(row.median_lesson_active_ms))}</Cell>
      <Cell worse={w('p90_lesson_active_ms')}>{orDash(formatMinutes(row.p90_lesson_active_ms))}</Cell>
      <td className={`${C.td} min-w-[200px] align-middle`}>
        <TimeBar med={row.median_lesson_active_ms} p90={row.p90_lesson_active_ms} scale={scale} target={target} />
      </td>
      <Cell worse={w('lessons_over_target_rate')} title={pctTitle(row.lessons_over_target_rate)}>
        {orDash(pct(row.lessons_over_target_rate))}
      </Cell>
      <Cell worse={w('lesson_exit_rate')} title={pctTitle(row.lesson_exit_rate)}>
        {orDash(pct(row.lesson_exit_rate))}
      </Cell>
    </tr>
  );
};

/** p90 desc; blanks then suppressed last. The server sorts the same way; this is a guard. */
export function sortDrills(drills: DrillTime[]): DrillTime[] {
  return drills
    .map((d, i) => ({ d, i }))
    .sort((a, b) => {
      const sa = a.d.suppressed ? 1 : 0;
      const sb = b.d.suppressed ? 1 : 0;
      if (sa !== sb) return sa - sb;
      const pa = a.d.suppressed ? null : a.d.p90_lesson_active_ms;
      const pb = b.d.suppressed ? null : b.d.p90_lesson_active_ms;
      if (pa == null && pb == null) return a.i - b.i;
      if (pa == null) return 1;
      if (pb == null) return -1;
      return pb - pa || a.i - b.i;
    })
    .map((x) => x.d);
}

const DrillRow: React.FC<{ row: DrillTime; scale: number; target: number }> = ({ row, scale, target }) => {
  const name = (
    <th scope="row" className={`${C.tdFrozen} bg-white group-hover:bg-cream w-[220px] min-w-[220px] max-w-[220px] text-left`}>
      <span className="block text-sm font-semibold text-ink">{row.drill_name}</span>
      <span className="block text-xs text-inkMuted truncate" title={row.lesson_name}>
        {row.lesson_name}
      </span>
    </th>
  );
  if (row.suppressed) {
    return (
      <tr className="bg-white group" data-testid="drill-row" data-suppressed="true">
        {name}
        <td colSpan={6} className={`${C.td} text-sm`}>
          <SuppressedValue inline />
        </td>
      </tr>
    );
  }
  const over = row.median_lesson_active_ms != null && row.median_lesson_active_ms > target;
  return (
    <tr className="bg-white hover:bg-cream group text-ink" data-testid="drill-row">
      {name}
      <td className={`${C.tdNum} text-sm`}>{orDash(row.lessons_completed)}</td>
      <td className={`${C.tdNum} text-sm ${over ? 'font-bold' : ''}`}>{orDash(formatMinutes(row.median_lesson_active_ms))}</td>
      <td className={`${C.tdNum} text-sm font-bold`}>{orDash(formatMinutes(row.p90_lesson_active_ms))}</td>
      <td className={`${C.td} min-w-[200px] align-middle`}>
        <TimeBar med={row.median_lesson_active_ms} p90={row.p90_lesson_active_ms} scale={scale} target={target} />
      </td>
      <td className={`${C.tdNum} text-sm`} title={pctTitle(row.first_try_correct_rate)}>
        {orDash(pct(row.first_try_correct_rate))}
      </td>
      <td className={`${C.tdNum} text-sm`}>
        {row.flagged_questions ? (
          <span className={C.flagChip}>{STORES_COPY.flagged(row.flagged_questions)}</span>
        ) : (
          <span className="text-inkMuted">0</span>
        )}
      </td>
    </tr>
  );
};

const StoresAndLessons: React.FC<{ data: TimeResponse; dim?: boolean }> = ({ data, dim = false }) => {
  const target = data.target_lesson_ms;
  const targetLabel = formatTargetMinutes(target);
  const p90 = (r: LocationTime | OverallTime | DrillTime) => (r.suppressed ? null : r.p90_lesson_active_ms);
  const storeScale = timeScaleMax([...data.locations, data.overall].map(p90));
  const drills = sortDrills(data.drills);
  const drillScale = timeScaleMax(drills.map(p90));
  const sub = STORES_COPY.storesSub(targetLabel);

  return (
    <div className={dim ? 'opacity-60' : ''} data-testid="time-content">
      <section className={C.section} aria-labelledby="qa-stores" data-testid="stores-section">
        <h2 id="qa-stores" className={C.h2}>
          {STORES_COPY.storesTitle}
        </h2>
        <p className={`${C.sub} mb-3`}>
          {sub.a}
          <b className="text-ink">{sub.b}</b>
          {sub.c}
        </p>
        <div className="text-xs text-inkMuted flex flex-wrap items-center gap-x-4 gap-y-1 mb-3">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-5 h-2.5 rounded-full bg-ink/70" />
            {STORES_COPY.legendTypical}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-5 h-2.5 rounded-full bg-ink/20" />
            {STORES_COPY.legendP90}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-0.5 h-3.5 bg-gold" />
            <b className="text-ink">{STORES_COPY.legendTarget(targetLabel)}</b>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-4 h-3 rounded-sm bg-warning-surface border border-softBorder" />
            <span aria-hidden="true" className="text-[10px] text-ink">
              ▲
            </span>
            {STORES_COPY.legendWorse}
          </span>
        </div>
        <div className="min-w-0">
          <div className={C.scroll}>
            <table className={C.table} data-testid="stores-table">
              <thead>
                <tr>
                  <th scope="col" className={`${C.thFrozen} w-[180px] min-w-[180px] text-inkMuted`}>
                    Store
                  </th>
                  {thNum(['Learners'], 'Different people who completed or started a lesson')}
                  {thNum(['Lessons', 'completed'], 'Lessons finished in range')}
                  {thNum(['Right on', 'first try'], 'Share of answers right on the first try')}
                  {thNum(['Typical', 'lesson'], 'Median active time per completed lesson')}
                  {thNum(['1 in 10', 'took over'], '90th percentile active time per completed lesson')}
                  <th scope="col" className={`${C.th} text-left text-inkMuted min-w-[200px]`}>
                    <ScaleAxis scale={storeScale} target={target} />
                  </th>
                  {thNum(['Over', targetLabel], `Share of completed lessons that took longer than ${targetLabel}`)}
                  {thNum(['Left', 'unfinished'], 'Share of lessons started and left without finishing')}
                </tr>
              </thead>
              <tbody>
                <StoreRow row={data.overall} name={STORES_COPY.allStores} overall={null} scale={storeScale} target={target} />
                {data.locations.map((l) => (
                  <StoreRow key={l.location_id} row={l} name={l.location_name} overall={data.overall} scale={storeScale} target={target} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {data.locations.length === 0 && <p className="text-sm text-inkMuted mt-2">{STORES_COPY.noLocations}</p>}
      </section>

      <section className={C.section} aria-labelledby="qa-lessons" data-testid="lessons-section">
        <h2 id="qa-lessons" className={C.h2}>
          {STORES_COPY.lessonsTitle}
        </h2>
        <p className={`${C.sub} mb-3`}>{STORES_COPY.lessonsSub}</p>
        {drills.length === 0 ? (
          <p className="text-sm text-inkMuted">{STORES_COPY.noLessons}</p>
        ) : (
          <div className="min-w-0">
            <div className={C.scroll}>
              <table className={C.table} data-testid="lessons-table">
                <thead>
                  <tr>
                    <th scope="col" className={`${C.thFrozen} w-[220px] min-w-[220px] text-inkMuted`}>
                      Lesson
                    </th>
                    {thNum(['Completed'], 'Lessons finished in range')}
                    {thNum(['Typical', 'lesson'], 'Median active time')}
                    {thNum(['1 in 10', 'took over'], '90th percentile active time', true)}
                    <th scope="col" className={`${C.th} text-left text-inkMuted min-w-[200px]`}>
                      <ScaleAxis scale={drillScale} target={target} />
                    </th>
                    {thNum(['Right on', 'first try'], 'Share of answers right on the first try')}
                    {thNum(['Flagged', 'questions'], 'Questions in this lesson with at least one flag')}
                  </tr>
                </thead>
                <tbody>
                  {drills.map((d) => (
                    <DrillRow key={d.drill_id} row={d} scale={drillScale} target={target} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default StoresAndLessons;
